/**
 * useSyncHealth — the one place that answers "is my data actually safe?"
 *
 * The status bar used to say `Sync: last 4:05 PM`. That is the time of the
 * last SUCCESS, which tells a shopkeeper nothing about whether three invoices
 * are stuck behind it — and in this product, stuck-and-silent is exactly how
 * every sync bug has presented. A till that has not synced since this morning
 * looks identical to one that synced a second ago.
 *
 * Three signals, because they fail independently:
 *
 *   pending      work written locally that has not reached the server
 *   persistence  whether the browser has promised not to evict that work
 *   reconcile    what the SERVER says it holds, which is the only check the
 *                client's own bookkeeping cannot fake
 *
 * The severity they combine into is deliberately conservative: a till mid-cycle
 * with two unsynced rows is normal and must not cry wolf, because a warning
 * nobody believes is worse than no warning at all.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getPendingWork } from '@/db';
import { requestPersistentStorage } from '@/db/persistentStorage';
import { reconcileStore, type ReconcileDiff } from './reconcile';
import { peekRenumbering, clearRenumbering, describeRenumbering, type RenumberOutcome } from './invoiceRenumber';

/** Older than this and unsynced work stops being "mid-cycle" and starts being a problem. */
export const STALE_AFTER_MS = 10 * 60 * 1000;

/** How often to ask the server what it holds. Cheap, but not free. */
export const RECONCILE_EVERY_MS = 15 * 60 * 1000;

/** How often the local database is scanned for unsynced work. */
const DB_POLL_MS = 60 * 1000;

/** How often the displayed age re-renders. No database work — just a repaint. */
const AGE_TICK_MS = 30 * 1000;

export type SyncSeverity = 'ok' | 'info' | 'warn' | 'alert';

export interface SyncHealth {
  pending: number;
  /** Epoch ms of the oldest unsynced record. */
  oldestAt: number | null;
  /** Milliseconds the oldest unsynced record has been waiting. */
  oldestAgeMs: number;
  /** False when the browser may evict this till's database. */
  storagePersisted: boolean | null;
  /** Null until a check has run — never confuse "not checked" with "clean". */
  reconcile: ReconcileDiff | null;
  /** Bills whose number changed because another device got there first. */
  renumbered: RenumberOutcome[];
  /** Acknowledge the renumbering — the operator has reprinted. */
  acknowledgeRenumbering: () => void;
  severity: SyncSeverity;
  /** One line, in a shopkeeper's words. Null when there is nothing to say. */
  message: string | null;
  /** The oldest wait, already worded — "4 minutes". Empty when nothing waits. */
  ageLabel: string;
  refresh: () => void;
}

/** "4 minutes", "2 hours" — vague on purpose; precision is not the point. */
export function describeAge(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'less than a minute';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

/**
 * Turn the raw signals into one severity and one sentence.
 *
 * Pure, so the rules can be tested without a database, a browser or a clock.
 * The ordering matters: a sale the server has never heard of outranks
 * everything else, because it is the only one that is already a loss rather
 * than a risk.
 */
export function assessSyncHealth(input: {
  pending: number;
  oldestAgeMs: number;
  storagePersisted: boolean | null;
  reconcile: ReconcileDiff | null;
  renumbered?: readonly RenumberOutcome[];
}): { severity: SyncSeverity; message: string | null } {
  const { pending, oldestAgeMs, storagePersisted, reconcile } = input;
  const renumbered = input.renumbered ?? [];


  const missing = reconcile?.missingOnServer.length ?? 0;
  if (missing > 0) {
    return {
      severity: 'alert',
      message:
        `${missing} ${missing === 1 ? 'bill has' : 'bills have'} not reached the server. ` +
        `They exist only on this device — do not clear this browser's data.`,
    };
  }

  // Below a missing sale, above anything merely pending. A renumbered bill IS
  // on the server, so the data is safe — but it is already out in the world:
  // the customer holds a bill whose number no longer matches the books, and
  // only a person can put that right.
  if (renumbered.length > 0) {
    return { severity: 'warn', message: describeRenumbering(renumbered) };
  }

  if (pending > 0 && oldestAgeMs >= STALE_AFTER_MS) {
    return {
      severity: 'alert',
      message:
        `${pending} ${pending === 1 ? 'change has' : 'changes have'} been waiting to sync for ` +
        `${describeAge(oldestAgeMs)}. Check this device's internet connection.`,
    };
  }

  // Storage permanence is only raised ALONGSIDE unsynced work, never on its
  // own.
  //
  // The first version showed a standing banner whenever the browser had not
  // granted permanent storage, which on a fresh profile is most of the time.
  // Read plainly it said "do not use the web version" — to every web user,
  // permanently, including the overwhelming majority whose data is fully
  // synced. That is both alarming and wrong: once a sale is on the server,
  // eviction costs nothing, because the till simply pulls it back.
  //
  // Evictable storage only endangers work that has NOT reached the server yet.
  // So it is a clause on that message, not a message of its own.
  if (pending > 0 && storagePersisted === false) {
    return {
      severity: 'warn',
      message:
        `${pending} ${pending === 1 ? 'change is' : 'changes are'} not on the server yet. ` +
        `Keep this device online until it syncs.`,
    };
  }

  if (pending > 0) {
    return {
      severity: 'info',
      message: `${pending} ${pending === 1 ? 'change' : 'changes'} waiting to sync.`,
    };
  }

  return { severity: 'ok', message: null };
}

export function useSyncHealth(
  activeStoreId: string | null,
  /** Bump this after a sync finishes so the numbers refresh immediately. */
  syncTick: unknown,
): SyncHealth {
  const [pending, setPending] = useState(0);
  const [oldestAt, setOldestAt] = useState<number | null>(null);
  const [storagePersisted, setStoragePersisted] = useState<boolean | null>(null);
  const [reconcile, setReconcile] = useState<ReconcileDiff | null>(null);
  const [renumbered, setRenumbered] = useState<RenumberOutcome[]>([]);
  const [, forceTick] = useState(0);
  const lastReconcileAt = useRef(0);

  const readPending = useCallback(() => {
    void getPendingWork()
      .then(({ count, oldestAt: at }) => {
        setPending(count);
        setOldestAt(at);
      })
      .catch(() => undefined);
  }, []);

  // Two separate clocks, because they cost very different amounts.
  //
  // The database is read on a slow timer and after every sync. The displayed
  // AGE just needs the component to re-render, which costs nothing, so it gets
  // its own faster tick rather than dragging a table scan along with it.
  useEffect(() => {
    readPending();
    const id = setInterval(readPending, DB_POLL_MS);
    return () => clearInterval(id);
  }, [readPending, syncTick]);

  // Renumberings are recorded by the sync layer, which cannot render. Picked up
  // after each cycle so the shop actually sees what happened to its bill.
  useEffect(() => {
    setRenumbered(peekRenumbering(activeStoreId));
  }, [syncTick, activeStoreId]);

  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), AGE_TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Storage permanence: asked once at boot elsewhere; read here for display.
  useEffect(() => {
    void requestPersistentStorage()
      .then((r) => setStoragePersisted(r.supported ? r.persisted : null))
      .catch(() => setStoragePersisted(null));
  }, []);

  // Reconcile on a long timer, and after a sync — but never more often than
  // RECONCILE_EVERY_MS, so a busy counter syncing constantly does not turn this
  // into a second sync loop.
  useEffect(() => {
    if (!activeStoreId) return;
    let alive = true;

    const run = () => {
      if (Date.now() - lastReconcileAt.current < RECONCILE_EVERY_MS) return;
      lastReconcileAt.current = Date.now();
      // Wait for the browser to be idle. A counter mid-sale must never be
      // competing with a background audit for the main thread; if the till
      // stays busy, the check simply happens on the next pass.
      const start = () =>
        void reconcileStore(activeStoreId).then((diff) => {
          // null means "could not check". Keep the previous answer rather than
          // replacing a real warning with a reassuring blank.
          if (alive && diff) setReconcile(diff);
        });
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(start, { timeout: 30_000 });
      } else {
        setTimeout(start, 0);
      }
    };

    run();
    const id = setInterval(run, RECONCILE_EVERY_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [activeStoreId, syncTick]);

  const oldestAgeMs = oldestAt == null ? 0 : Math.max(0, Date.now() - oldestAt);
  const { severity, message } = assessSyncHealth({
    pending,
    oldestAgeMs,
    storagePersisted,
    reconcile,
    renumbered,
  });

  return {
    pending,
    oldestAt,
    oldestAgeMs,
    ageLabel: oldestAt == null ? '' : describeAge(oldestAgeMs),
    storagePersisted,
    reconcile,
    renumbered,
    acknowledgeRenumbering: () => {
      clearRenumbering(activeStoreId);
      setRenumbered([]);
    },
    severity,
    message,
    refresh: readPending,
  };
}
